import {
  AliasType,
  InsuredEntitySubmissionNode,
  SubmissionStatus,
  useInsuredEntitySubmissionsQuery,
} from '@converge/graphql';
import {
  CustomizedTable,
  TableColumn,
  createTextColumn,
} from '../../../presentation';
import { displayFormatDateUTC } from '@converge/utils';
import { useState } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { ConvergeNavigationUrls } from '../../../contexts';
import { SubmissionStatusBadge } from '../../submission/submission-status-badge';

type SubmissionRow = InsuredEntitySubmissionNode & Record<string, unknown>;

const MAX_TEXT_LENGTH = 12;

const truncateText = (text: string): string =>
  text.length > MAX_TEXT_LENGTH
    ? `${text.substring(0, MAX_TEXT_LENGTH)}...`
    : text;

const RENEWAL_TYPE_LABELS: Record<string, string> = {
  AUTO_QUOTE_RENEWAL: 'Auto Quote Renewal',
  AUTO_RENEWAL: 'Auto Renewal',
  CONDITIONAL_RENEWAL: 'Conditional Renewal',
  NON_RENEWAL: 'Non Renewal',
  TRADITIONAL_RENEWAL: 'Traditional Renewal',
};

const buildColumns = (): TableColumn<SubmissionRow>[] => [
  createTextColumn<SubmissionRow>('referenceId', 'Submission Number', {
    sortable: false,
    flex: 1.5,
    headerTestId: 'referenceId-header',
    render: (_, row, rowIndex) => (
      <span
        data-testid={rowIndex != null ? `referenceId_${rowIndex}` : undefined}
      >
        <Link
          data-testid={
            rowIndex != null
              ? `navigate-submission-link_${rowIndex}`
              : undefined
          }
          to={ConvergeNavigationUrls.underwritingSubmissionDetail({
            submissionUuid: row.uuid,
          })}
          style={{
            color: 'black',
            fontWeight: 600,
            textDecoration: 'underline',
          }}
        >
          {row.referenceId ?? '—'}
        </Link>
      </span>
    ),
  }),
  createTextColumn<SubmissionRow>('renewal', 'Submission Type', {
    sortable: false,
    headerTestId: 'renewal-header',
    render: (_, row, rowIndex) => {
      const text =
        row.renewal && row.renewalType
          ? RENEWAL_TYPE_LABELS[row.renewalType as string] ??
            (row.renewalType as string)
          : 'New Business';
      return (
        <Tooltip
          title={text.length > MAX_TEXT_LENGTH ? text : ''}
          placement="top"
        >
          <span
            data-testid={rowIndex != null ? `renewal_${rowIndex}` : undefined}
          >
            {truncateText(text)}
          </span>
        </Tooltip>
      );
    },
  }),
  createTextColumn<SubmissionRow>('type', 'Policy Type', {
    sortable: false,
    flex: 0.7,
    headerTestId: 'type-header',
    render: (_, row, rowIndex) => (
      <span data-testid={rowIndex != null ? `type_${rowIndex}` : undefined}>
        {row.type === 'PRIMARY'
          ? 'Primary'
          : row.type === 'EXCESS'
          ? 'Excess'
          : '—'}
      </span>
    ),
  }),
  createTextColumn<SubmissionRow>('brokerEntity', 'Brokerage', {
    sortable: false,
    flex: 1.3,
    headerTestId: 'brokerEntity-header',
    render: (_, row, rowIndex) => {
      const name =
        (
          row.brokerEntity as InsuredEntitySubmissionNode['brokerEntity']
        )?.node?.aliases?.edges?.find(
          (e) => e?.node?.primary && e?.node?.type === AliasType.LegalName
        )?.node?.name ?? '—';
      return (
        <Tooltip
          title={name.length > MAX_TEXT_LENGTH ? name : ''}
          placement="top"
        >
          <span
            data-testid={
              rowIndex != null ? `brokerEntity_${rowIndex}` : undefined
            }
          >
            {truncateText(name)}
          </span>
        </Tooltip>
      );
    },
  }),
  createTextColumn<SubmissionRow>('persons', 'Broker', {
    sortable: false,
    flex: 1.3,
    headerTestId: 'persons-header',
    render: (_, row, rowIndex) => {
      const p = (
        row.persons as InsuredEntitySubmissionNode['persons']
      )?.edges?.find(
        (e) => e?.node?.type?.toLowerCase() === 'agency_contact'
      )?.node;
      const fullName = p
        ? [p.firstName, p.lastName].filter(Boolean).join(' ') || '—'
        : '—';
      return (
        <Tooltip
          title={fullName.length > MAX_TEXT_LENGTH ? fullName : ''}
          placement="top"
        >
          <span
            data-testid={rowIndex != null ? `persons_${rowIndex}` : undefined}
          >
            {truncateText(fullName)}
          </span>
        </Tooltip>
      );
    },
  }),
  createTextColumn<SubmissionRow>('policyStartDate', 'Effective Date', {
    sortable: false,

    headerTestId: 'policyStartDate-header',
    render: (_, row, rowIndex) => (
      <span
        data-testid={
          rowIndex != null ? `policyStartDate_${rowIndex}` : undefined
        }
      >
        {displayFormatDateUTC((row.policyStartDate as string) ?? '') || '—'}
      </span>
    ),
  }),
  createTextColumn<SubmissionRow>('status', 'Status', {
    sortable: false,

    align: 'center',
    headerTestId: 'status-header',
    render: (_, row, rowIndex) => (
      <Box data-testid={rowIndex != null ? `status_${rowIndex}` : undefined}>
        <SubmissionStatusBadge
          status={row.status as SubmissionStatus}
          uuid={row.uuid}
          createdAt={(row.createdAt as string) ?? ''}
        />
      </Box>
    ),
  }),
];

interface InsuredSubmissionsTableProps {
  entityUuid: string;
}

export const InsuredSubmissionsTable = ({
  entityUuid,
}: InsuredSubmissionsTableProps) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, loading } = useInsuredEntitySubmissionsQuery({
    insuredEntityUuid: entityUuid,
    pageNumber: page,
    pageSize,
  });

  const rows = (data?.submissions.edges.map((e) => e.node) ??
    []) as SubmissionRow[];
  const total = data?.submissions.pageInfo.totalResults ?? 0;

  if (loading && !data) {
    return (
      <Box
        data-testid="loading-spinner"
        aria-busy={true}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
        }}
      >
        <CircularProgress size={40} sx={{ color: '#23a892' }} />
      </Box>
    );
  }

  return (
    <Box style={{ margin: '20px 25px' }}>
      <CustomizedTable<SubmissionRow>
        columns={buildColumns()}
        data={rows}
        loading={false}
        maxHeight="calc(80vh - 18vh)"
        enableSorting={false}
        enablePagination
        rowsPerPageOptions={[10, 25, 50]}
        defaultRowsPerPage={pageSize}
        currentPage={page}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        message="There are no submissions for this insured."
        rowTestIdPrefix="submission"
      />
    </Box>
  );
};
